/**
 * video / audio renderer（EP-2・M7b）。誌面に動画・音声を埋め込む。
 *
 * provider は 3 種（サーバ `Overlay\OverlayInput` と対）:
 *   self          … 直リンクを `<video>`/`<audio>` で再生（poster はネイティブの初期表示）
 *   youtube/vimeo … **クリックで初めて iframe をマウント**する（未活性＝ポスター）。
 *                    以後 iframe は捨てない — めくって戻っても再生位置が保たれる。
 *
 * ページから外れたら一時停止する（要素は保持）。self は `pause()`、外部は postMessage。
 * どちらも要素は破棄しないため、DOM 再親付けによるリロードは起きない（M7a の層設計が前提）。
 */
import { cls } from '../../brand';
import { embedOrigin, embedUrl, pauseMessage, type EmbedProvider } from '../embed';
import type { OverlayContext, OverlayHandle, OverlayItem, OverlayRenderer } from '../registry';

/** 字幕トラック 1 件（サーバの `Overlay\OverlayInput::tracks` が検証した形）。 */
interface MediaTrack {
  src?: string;
  srclang?: string;
  label?: string;
  kind?: string;
  default?: boolean;
}

interface MediaPayload {
  provider?: string;
  src?: string;
  poster?: string;
  loop?: boolean;
  label?: string;
  /** 字幕（自前ホストの動画のみ。外部プレーヤの字幕は配信元が持つ）。 */
  tracks?: MediaTrack[];
}

/**
 * 外部プレーヤの iframe に与える権限（NFR-SEC-01）。
 * 再生に要る最小限 — スクリプト・自ドメインの Cookie/設定（same-origin）・
 * キャスト/別画面（presentation）・「別タブで見る」（popups）。フォーム送信と
 * トップレベル遷移は与えない。全画面は `allow` 属性（Permissions Policy）側で許可する。
 */
const MEDIA_SANDBOX =
  'allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox';

/** 再生を止められるプレーヤ（visibilityChanged で使う）。 */
export interface MediaPlayer {
  pause(): void;
}

export const videoRenderer: OverlayRenderer = (el, item, context) => {
  const player = createMediaPlayer(el, item, context, 'video');
  return pausingHandle(player);
};

export const audioRenderer: OverlayRenderer = (el, item, context) => {
  const player = createMediaPlayer(el, item, context, 'audio');
  return pausingHandle(player);
};

/** ページから外れたら止めるだけのハンドル（要素は層が保持する）。 */
export function pausingHandle(player: MediaPlayer | null): OverlayHandle {
  return {
    visibilityChanged: (visible) => {
      if (!visible) player?.pause();
    },
  };
}

/**
 * `el` にプレーヤを組み立てて返す。payload が壊れていれば何も描かず null。
 * media_note からも同じ関数で中身を作る（誌面直置きかポップアップ内かの違いだけ）。
 */
export function createMediaPlayer(
  el: HTMLElement,
  item: OverlayItem,
  context: OverlayContext,
  kind: 'video' | 'audio',
): MediaPlayer | null {
  const payload = item.payload as MediaPayload;
  const src = typeof payload.src === 'string' ? payload.src : '';
  if (src === '') return null;

  if (kind === 'audio') {
    return selfMedia(el, context.doc, 'audio', src, payload);
  }
  const provider = payload.provider;
  if (provider === 'self') {
    return selfMedia(el, context.doc, 'video', src, payload);
  }
  if (provider === 'youtube' || provider === 'vimeo') {
    return embedMedia(el, context, provider, src, payload);
  }
  return null;
}

/** 自前ホスト（直リンク）の `<video>`/`<audio>`。ネイティブ制御・poster をそのまま使う。 */
function selfMedia(
  el: HTMLElement,
  doc: Document,
  tag: 'video' | 'audio',
  src: string,
  payload: MediaPayload,
): MediaPlayer {
  const media = doc.createElement(tag);
  media.className = cls('overlay-media');
  media.controls = true;
  media.preload = 'none';
  media.src = src;
  if (payload.loop === true) media.loop = true;
  if (tag === 'video' && typeof payload.poster === 'string') {
    (media as HTMLVideoElement).poster = payload.poster;
  }
  appendTracks(doc, media, payload.tracks);
  el.appendChild(media);
  return { pause: () => media.pause() };
}

/**
 * 字幕トラック（WCAG 2.1 の 1.2.2 キャプション）。自前ホストの動画は製品側が
 * `<video>` を組み立てるので、ここが無いと導入者に字幕を付ける手段がない。
 * 検証（URL・言語タグ・種別）はサーバの `Overlay\OverlayInput` 側で済んでいる。
 */
function appendTracks(doc: Document, media: HTMLMediaElement, tracks: unknown): void {
  if (!Array.isArray(tracks) || tracks.length === 0) return;

  // 別オリジンの字幕は CORS 越しにしか読めず、`crossorigin` が無いとブラウザが
  // 落とす。同一オリジンのときは付けない — 付けると動画本体まで CORS 必須になり、
  // いまある配信（CORS ヘッダ無し）が壊れるため。
  const sameOrigin = (url: string): boolean => {
    try {
      return new URL(url, doc.baseURI).origin === doc.location?.origin;
    } catch {
      return true;
    }
  };
  const list = tracks as MediaTrack[];
  if (list.some((track) => typeof track.src === 'string' && !sameOrigin(track.src))) {
    media.crossOrigin = 'anonymous';
  }

  for (const track of list) {
    if (typeof track.src !== 'string' || track.src === '') continue;
    const el = doc.createElement('track');
    el.src = track.src;
    el.kind = track.kind ?? 'captions';
    if (typeof track.srclang === 'string') el.srclang = track.srclang;
    if (typeof track.label === 'string') el.label = track.label;
    if (track.default === true) el.default = true;
    media.appendChild(el);
  }
}

/**
 * 外部プレーヤ（YouTube/Vimeo）。**ポスターのクリックで初めて iframe を作る**。
 * 一度作った iframe は保持し、以後は postMessage で一時停止するだけ。
 */
function embedMedia(
  el: HTMLElement,
  context: OverlayContext,
  provider: EmbedProvider,
  id: string,
  payload: MediaPayload,
): MediaPlayer {
  const doc = context.doc;
  let frame: HTMLIFrameElement | null = null;

  const poster = doc.createElement('button');
  poster.type = 'button';
  poster.className = cls('overlay-poster');
  poster.setAttribute(
    'aria-label',
    payload.label ?? context.translate('overlay.play'),
  );
  if (typeof payload.poster === 'string') {
    poster.style.backgroundImage = `url("${encodeURI(payload.poster)}")`;
  }
  const icon = doc.createElement('span');
  icon.className = cls('overlay-poster-icon');
  icon.setAttribute('aria-hidden', 'true');
  poster.appendChild(icon);

  poster.addEventListener('click', () => {
    frame = doc.createElement('iframe');
    frame.className = cls('overlay-media');
    frame.setAttribute('sandbox', MEDIA_SANDBOX);
    frame.src = embedUrl(provider, id);
    frame.allow = 'autoplay; fullscreen; encrypted-media; picture-in-picture';
    frame.setAttribute('allowfullscreen', 'true');
    if (payload.label !== undefined) frame.title = payload.label;
    poster.replaceWith(frame);
  });
  el.appendChild(poster);

  return {
    pause: () => {
      frame?.contentWindow?.postMessage(pauseMessage(provider), embedOrigin(provider));
    },
  };
}
